import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Phone, Mail, MapPin, Edit, Trash2, IndianRupee, Percent, ArrowRightCircle, Download, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_TYPES = [
  "Housing Loan", "Personal Loan", "Mortgage Loan", "Business Loan",
  "Car Loan", "Loan Take Over", "LAP", "Poultry Loan"
];

const CUSTOMER_STATUSES = ["New", "In Progress", "Converted"];

const statusColors = {
  'New': 'bg-blue-100 text-blue-700 border-blue-200',
  'In Progress': 'bg-amber-100 text-amber-700 border-amber-200',
  'Converted': 'bg-green-100 text-green-700 border-green-200'
};

const CustomersPage = () => {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([...LOAN_TYPES]);
  const [selectedStatuses, setSelectedStatuses] = useState([...CUSTOMER_STATUSES]);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    loan_type: '',
    status: 'New',
    fee_paid: '',
    commission_paid: '',
    remarks: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Error fetching customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        fee_paid: parseFloat(formData.fee_paid) || 0,
        commission_paid: parseFloat(formData.commission_paid) || 0
      };
      
      if (editingCustomer) {
        const res = await axios.put(`${API}/customers/${editingCustomer.id}`, payload);
        if (res.data?.loan_created) {
          toast.success(`Customer updated & Loan File auto-created: ${res.data.file_number}`);
        } else {
          toast.success('Customer updated successfully');
        }
      } else {
        await axios.post(`${API}/customers`, payload);
        toast.success('Customer created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone,
      email: customer.email || '',
      address: customer.address || '',
      loan_type: customer.loan_type,
      status: customer.status || 'New',
      fee_paid: customer.fee_paid?.toString() || '',
      commission_paid: customer.commission_paid?.toString() || '',
      remarks: customer.remarks || ''
    });
    setIsDialogOpen(true);
  };

  const handleConvertToLoan = async (customerId) => {
    if (!window.confirm('Customer चा status "Converted" केल्यावर नवीन Loan File तयार होईल. Proceed करायचे का?')) return;
    try {
      const response = await axios.put(`${API}/customers/${customerId}/convert`);
      toast.success(`Loan File तयार झाली: ${response.data.file_number}`);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error converting customer');
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${API}/customers/${customerId}`);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Error deleting customer');
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      loan_type: '',
      status: 'New',
      fee_paid: '',
      commission_paid: '',
      remarks: ''
    });
  };

  // Toggle functions for filters
  const toggleType = (type) => {
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };
  const toggleStatus = (status) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;

  const exportToExcel = () => {
    const exportData = filteredCustomers.map(customer => ({
      'Name': customer.name,
      'Phone': customer.phone,
      'Email': customer.email || '-',
      'Address': customer.address || '-',
      'Loan Type': customer.loan_type,
      'Status': customer.status || 'New',
      'Fee (₹)': customer.fee_paid || 0,
      'Commission (₹)': customer.commission_paid || 0,
      'Remarks': customer.remarks || '-',
      'Created Date': customer.created_at ? new Date(customer.created_at).toLocaleDateString('en-IN') : '-'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Customers');
    
    const fileName = `Customers_Report_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('Report exported successfully');
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm);
    const matchesType = selectedTypes.includes(customer.loan_type);
    const matchesStatus = selectedStatuses.includes(customer.status || 'New');
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="customers-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Customers</h1>
          <p className="text-slate-500 mt-1">Manage customer information</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={exportToExcel}
            data-testid="export-customers-btn"
            className="border-brand-navy text-brand-navy hover:bg-brand-navy/5"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            data-testid="create-customer-btn"
            className="bg-brand-navy text-white hover:bg-brand-navy/90 shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-customers"
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {/* Type Filter Panel */}
          {showTypeFilter && (
            <div className="border-b p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-700">Select Loan Types:</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedTypes([...LOAN_TYPES])}>All</Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedTypes([])}>Clear</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {LOAN_TYPES.map(type => (
                  <label key={type} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs ${selectedTypes.includes(type) ? 'bg-blue-100 border-blue-300' : 'bg-white opacity-50'}`}>
                    <input type="checkbox" checked={selectedTypes.includes(type)} onChange={() => toggleType(type)} className="w-3 h-3" />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          )}
          {/* Status Filter Panel */}
          {showStatusFilter && (
            <div className="border-b p-4 bg-green-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-700">Select Status:</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedStatuses([...CUSTOMER_STATUSES])}>All</Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedStatuses([])}>Clear</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {CUSTOMER_STATUSES.map(status => (
                  <label key={status} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs ${selectedStatuses.includes(status) ? statusColors[status] : 'bg-white opacity-50'}`}>
                    <input type="checkbox" checked={selectedStatuses.includes(status)} onChange={() => toggleStatus(status)} className="w-3 h-3" />
                    {status}
                  </label>
                ))}
              </div>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">Name</TableHead>
                <TableHead className="font-semibold text-slate-600">Phone</TableHead>
                <TableHead className="font-semibold text-slate-600 cursor-pointer hover:bg-slate-100" onClick={() => setShowTypeFilter(!showTypeFilter)}>
                  <div className="flex items-center gap-1">
                    Loan Type
                    <Filter className={`h-3 w-3 ${selectedTypes.length < LOAN_TYPES.length ? 'text-brand-navy' : 'text-slate-400'}`} />
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-slate-600 cursor-pointer hover:bg-slate-100" onClick={() => setShowStatusFilter(!showStatusFilter)}>
                  <div className="flex items-center gap-1">
                    Status
                    <Filter className={`h-3 w-3 ${selectedStatuses.length < CUSTOMER_STATUSES.length ? 'text-brand-navy' : 'text-slate-400'}`} />
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-slate-600">Remarks</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <TableRow key={customer.id} className="hover:bg-slate-50/50">
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap" data-testid={`customer-date-${customer.id}`}>
                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                  </TableCell>
                  <TableCell className="font-medium">{customer.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3 text-slate-400" />
                      {customer.phone}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-brand-navy/5 text-brand-navy border-brand-navy/20">
                      {customer.loan_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusColors[customer.status || 'New']} border`}>
                      {customer.status || 'New'}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate text-slate-600 text-sm">
                    {customer.remarks || '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {customer.status !== 'Converted' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleConvertToLoan(customer.id)}
                          data-testid={`convert-customer-${customer.id}`}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <ArrowRightCircle className="h-4 w-4 mr-1" />
                          Convert
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEdit(customer)}
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(customer.id)}
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredCustomers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                    No customers found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">
              {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
            </DialogTitle>
            <DialogDescription>
              {editingCustomer ? 'Update customer information' : 'Enter customer details'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="input-customer-name"
                placeholder="Enter full name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="input-customer-phone"
                  placeholder="Enter phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="input-customer-email"
                  placeholder="Enter email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Loan Type</Label>
              <Select
                value={formData.loan_type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, loan_type: v }))}
              >
                <SelectTrigger data-testid="select-customer-loan-type">
                  <SelectValue placeholder="Select loan type" />
                </SelectTrigger>
                <SelectContent>
                  {LOAN_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Address (Optional)</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                data-testid="input-customer-address"
                placeholder="Enter address"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger data-testid="select-customer-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {CUSTOMER_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fee (₹)</Label>
                <Input
                  type="number"
                  value={formData.fee_paid}
                  onChange={(e) => setFormData(prev => ({ ...prev, fee_paid: e.target.value }))}
                  data-testid="input-customer-fee"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Commission (₹)</Label>
                <Input
                  type="number"
                  value={formData.commission_paid}
                  onChange={(e) => setFormData(prev => ({ ...prev, commission_paid: e.target.value }))}
                  data-testid="input-customer-commission"
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                data-testid="input-customer-remarks"
                placeholder="Add notes or remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              data-testid="submit-customer-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!formData.name || !formData.phone || !formData.loan_type}
            >
              {editingCustomer ? 'Update Customer' : 'Add Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
